import { useApolloClient } from "@apollo/client";
import {
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalTitle,
} from "@msys/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Alert,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  ToggleButton,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ProjectType } from "../../../../clients/graphqlTypes.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import { useOnboardingFlowModalQuery } from "./OnboardingFlowModal.generated.js";
import { PROJECT_TYPE_ICONS } from "../../projects/projectTypeIcons.js";
import { useSetOrganisationOfferedServices } from "./useSetOrganisationOfferedServices.js";
import type {
  OnboardingFlowWizardAction,
  OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

const PROJECT_TYPE_ORDER: ProjectType[] = [
  "NEW",
  "REPAIR",
  "RENOVATION",
  "MAINTENANCE",
  "DIY",
  "OTHER",
];

interface Props {
  state: OnboardingFlowWizardState;
  dispatch: React.Dispatch<OnboardingFlowWizardAction>;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export const OnboardingFlowProjectTypesStage = ({
  state,
  dispatch,
  setStage,
}: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const query = useOnboardingFlowModalQuery({ client });
  const { projectTypeLabels } = useProjectTypes();
  const { commit, error, loading } = useSetOrganisationOfferedServices(
    state,
    setStage
  );

  const viewer = query.data?.viewer;

  // Defense-in-depth: CLIENT orgs reaching this stage via direct URL skip
  // straight to `success`. Primary CLIENT routing is the form-stage branch
  // (§11.4); this only catches deep-link / typed-URL / browser-history cases.
  React.useEffect(() => {
    if (!viewer) return;
    if (viewer.organisation.organisationType !== "CRAFTSMAN") {
      setStage("success");
    }
  }, [viewer, setStage]);

  if (!viewer) {
    return <LoadingSpinner />;
  }
  if (viewer.organisation.organisationType !== "CRAFTSMAN") {
    return null;
  }

  const selectedTypes = new Set(state.selectedProjectTypes);
  const hasTypes = state.selectedProjectTypes.length > 0;
  const showApplyToAll = hasTypes;
  const showSpecifyMode = hasTypes && state.applyToAll === "no";

  // CONTINUE routes based on which sub-state the user is in. SKIP and
  // CONTINUE-with-no-tiles share the "skip-project-types" path.
  const handleContinue = () => {
    if (!hasTypes) {
      void commit("skip-project-types");
      return;
    }
    if (state.applyToAll === "yes") {
      void commit("apply-to-all-yes");
      return;
    }
    if (state.applyToAll === "no" && state.specifyMode === "now") {
      setStage("specifyMatrix");
      return;
    }
    if (state.applyToAll === "no" && state.specifyMode === "later") {
      void commit("specify-later");
      return;
    }
    // Tiles selected but no Apply-to-all choice yet — disable until the user
    // resolves the sub-state. Handled by `continueDisabled` below.
  };

  const continueDisabled =
    loading ||
    (hasTypes && state.applyToAll === "unset") ||
    (hasTypes && state.applyToAll === "no" && state.specifyMode === "unset");

  return (
    <>
      <ModalTitle
        title={t("Which project types do you offer?", {
          ns: "OrganisationProfile",
        })}
      />
      <ModalContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            {t(
              "Pick the project types you cover. You can also skip and configure them later.",
              { ns: "OrganisationProfile" }
            )}
          </Typography>
          <Grid container spacing={2} columns={{ xs: 2, md: 3 }}>
            {PROJECT_TYPE_ORDER.map(projectType => (
              <Grid key={projectType} size={1}>
                <ProjectTypeTile
                  projectType={projectType}
                  label={projectTypeLabels[projectType]}
                  value={projectType}
                  selected={selectedTypes.has(projectType)}
                  onClick={() =>
                    dispatch({ type: "toggleProjectType", projectType })
                  }
                />
              </Grid>
            ))}
          </Grid>

          {showApplyToAll && (
            <FormControl>
              <FormLabel id="apply-to-all-label">
                {t(
                  "{count} categories selected. Are you offering these project types for all of them?",
                  {
                    ns: "OrganisationProfile",
                    count: state.selectedVerticals.length,
                  }
                )}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="apply-to-all-label"
                value={state.applyToAll === "unset" ? "" : state.applyToAll}
                onChange={(_, value) =>
                  dispatch({
                    type: "setApplyToAll",
                    value: value as "yes" | "no",
                  })
                }
              >
                <FormControlLabel
                  value="yes"
                  control={<Radio />}
                  label={t("Yes", { ns: "Global" })}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio />}
                  label={t("No", { ns: "Global" })}
                />
              </RadioGroup>
            </FormControl>
          )}

          {showSpecifyMode && (
            <FormControl>
              <FormLabel id="specify-mode-label">
                {t(
                  "Would you like to specify project types per category now or later?",
                  { ns: "OrganisationProfile" }
                )}
              </FormLabel>
              <RadioGroup
                row
                aria-labelledby="specify-mode-label"
                value={state.specifyMode === "unset" ? "" : state.specifyMode}
                onChange={(_, value) =>
                  dispatch({
                    type: "setSpecifyMode",
                    value: value as "now" | "later",
                  })
                }
              >
                <FormControlLabel
                  value="now"
                  control={<Radio />}
                  label={t("Specify now", { ns: "OrganisationProfile" })}
                />
                <FormControlLabel
                  value="later"
                  control={<Radio />}
                  label={t("Specify later", { ns: "OrganisationProfile" })}
                />
              </RadioGroup>
            </FormControl>
          )}

          {error && (
            <Alert severity="error">
              {error.message ||
                t("Something went wrong. Please try again.", {
                  ns: "Global",
                })}
            </Alert>
          )}
        </Stack>
      </ModalContent>
      <ModalActions
        actionButtons={[
          {
            label: t("Back", { ns: "Global" }),
            handleClick: () => setStage("verticals"),
            buttonProps: {
              color: "primary",
              variant: "text",
              startIcon: <ChevronLeftIcon />,
              disabled: loading,
            },
          },
          {
            label: t("Skip", { ns: "Global" }),
            handleClick: () => void commit("skip-project-types"),
            buttonProps: {
              color: "primary",
              variant: "text",
              endIcon: <KeyboardDoubleArrowRightIcon />,
              disabled: loading,
            },
          },
          {
            label: t("Continue", { ns: "Global" }),
            handleClick: handleContinue,
            buttonProps: {
              color: "primary",
              variant: "contained",
              endIcon: <ChevronRightIcon />,
              disabled: continueDisabled,
            },
          },
        ]}
      />
    </>
  );
};

interface ProjectTypeTileProps {
  projectType: ProjectType;
  label: string;
  value: ProjectType;
  selected: boolean;
  onClick: () => void;
}

const ProjectTypeTile = ({
  projectType,
  label,
  value,
  selected,
  onClick,
}: ProjectTypeTileProps) => {
  const Icon = PROJECT_TYPE_ICONS[projectType];

  return (
    <ToggleButton
      aria-label={label}
      aria-pressed={selected}
      data-project-type={projectType}
      value={value}
      selected={selected}
      onChange={onClick}
      sx={{
        flex: 1,
        width: "100%",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Icon fontSize="large" color={"inherit"} aria-hidden />
      <Typography variant="body2" color={"inherit"}>
        {label}
      </Typography>
    </ToggleButton>
  );
};
